import { Packer } from "docx";
import { createWorker, PSM } from "tesseract.js";
import { buildDocxDocument, placeImages, type ImageBlock, type OutPage } from "./docxBuild";
import { layoutDocument, type LayoutBlock, type ParagraphBlock } from "./pdfLayout";
import { readPageContent, type PageContent } from "./pdfPageContent";
import { extractImagesFromPage, renderPageToJpegBlob } from "./utils";
import { preprocessForOcr } from "./ocrImage";
import { allowsDevanagari, needsSecondReading, ocrParagraphs, readingScore, type OcrPageLike } from "./ocrText";
import { meaningfulAuthor, meaningfulTitle } from "./pdfMetadata";
import type { PreviewPage } from "./ConvertedPreview";

export type TesseractWorker = Awaited<ReturnType<typeof createWorker>>;

export class ConversionCancelled extends Error {
  constructor() {
    super("cancelled");
  }
}

export interface ConvertSummary {
  pages: number;
  tables: number;
  images: number;
  ocrPages: number;
  /** Pages kept as a picture because they had no readable text. */
  pictureOnlyPages: number;
  formValues: number;
  links: number;
  /** Things the person should know about; shown under the result. */
  warnings: string[];
}

export interface ConvertHooks {
  /** False once this run was stopped, reset or replaced. Checked after every await. */
  isCurrent: () => boolean;
  status: (message: string) => void;
  progress: (fraction: number) => void;
  /** Lets the caller terminate the OCR engine the moment the run is stopped. */
  registerWorker: (worker: TesseractWorker | null) => void;
}

export interface ConvertOptions {
  ocrLanguage: string;
  fallbackTitle: string;
}

export interface ConvertOutcome {
  blob: Blob;
  summary: ConvertSummary;
  preview: PreviewPage[];
}

/** A page with fewer characters than this after OCR is treated as a picture rather than text. */
const MIN_MEANINGFUL_OCR_CHARS = 40;
const OCR_BODY_SIZE = 11;

const bytesOf = async (blob: Blob) => new Uint8Array(await blob.arrayBuffer());

function ocrBlocks(paragraphs: string[]): ParagraphBlock[] {
  return paragraphs.map((text, i) => ({
    kind: "paragraph" as const,
    runs: [{ text, bold: false, italic: false, sizePt: OCR_BODY_SIZE }],
    topPt: i * 20,
    bottomPt: i * 20 + 12,
    leftPt: 72,
    rightPt: 540,
    indentPt: 0,
    spaceAfterPt: 8,
    sizePt: OCR_BODY_SIZE,
  }));
}

function countBlocks(blocks: LayoutBlock[]) {
  let tables = 0, links = 0;
  const countRuns = (runs: { link?: string }[]) => runs.forEach((r) => r.link && links++);
  for (const b of blocks) {
    if (b.kind === "table") {
      tables++;
      b.rows.forEach((r) => r.forEach((c) => countRuns(c.runs)));
    } else countRuns(b.runs);
  }
  return { tables, links };
}

/** The pages to convert as a sorted, de-duplicated list of 1-based page numbers (a Word file always follows the PDF's own order). */
export function sortedPageNumbers(zeroBased: number[]): number[] {
  return Array.from(new Set(zeroBased)).sort((a, b) => a - b).map((i) => i + 1);
}

/** A view of a pdf.js document that only has the chosen pages, so the converter can run on a page range unchanged. */
export function pdfSubset(pdf: any, pageNumbers: number[]): any {
  return {
    numPages: pageNumbers.length,
    getPage: (n: number) => pdf.getPage(pageNumbers[n - 1]),
    getMetadata: () => pdf.getMetadata(),
    destroy: () => pdf.destroy?.(),
  };
}

/**
 * The whole conversion. Each page is judged on its own: a page with a text layer goes through the
 * layout analysis; a page without one is read with OCR (or kept as a picture when there is nothing
 * to read); pictures on text pages are placed where they sat. Throws ConversionCancelled when the
 * run is no longer current.
 */
export async function convertPdfToWord(pdf: any, opts: ConvertOptions, hooks: ConvertHooks): Promise<ConvertOutcome | null> {
  const alive = () => {
    if (!hooks.isCurrent()) throw new ConversionCancelled();
  };
  const numPages: number = pdf.numPages;
  const contents: PageContent[] = [];
  const pageImages: ImageBlock[][] = [];
  const warnings: string[] = [];
  let imageFailures = 0;
  let renderedImages = 0;
  let scanBackgrounds = 0;
  let annotationFailures = 0;
  let worker: TesseractWorker | null = null;

  try {
    const info = await pdf.getMetadata().catch(() => null);
    alive();

    // Pass 1: read every page (text, styles, pictures).
    for (let i = 0; i < numPages; i++) {
      hooks.status(`Reading page ${i + 1} of ${numPages}…`);
      hooks.progress(i / (numPages * 2));
      const page = await pdf.getPage(i + 1);
      alive();
      const content = await readPageContent(page);
      alive();
      let blocks: ImageBlock[] = [];
      const found = await extractImagesFromPage(page).catch((err) => {
        console.warn("Couldn't read the pictures on page", i + 1, err);
        return { images: [], failed: 1 };
      });
      alive();
      imageFailures += found.failed;
      for (const img of found.images) {
        // A scan sitting behind an invisible text layer: the text is what the person wants.
        if (img.coversPage && content.hasText) {
          scanBackgrounds++;
          continue;
        }
        if (img.fromRender) renderedImages++;
        blocks.push({
          kind: "image",
          data: await bytesOf(img.blob),
          type: img.type,
          widthPt: img.position.widthPt,
          heightPt: img.position.heightPt,
          topPt: img.position.topPt,
          leftPt: img.position.xPt,
          rightPt: img.position.xPt + img.position.widthPt,
          description: `Picture from page ${i + 1} of the PDF`,
        });
        alive();
      }
      if (content.annotationsFailed) annotationFailures++;
      contents.push(content);
      pageImages.push(blocks);
    }

    const layout = layoutDocument(
      contents,
      pageImages.map((imgs) => imgs.map((b) => ({ left: b.leftPt, right: b.rightPt, top: b.topPt, bottom: b.topPt + b.heightPt }))),
    );

    // Pass 2: assemble each page's output; pages without text are read with OCR.
    const outPages: OutPage[] = [];
    const preview: PreviewPage[] = [];
    let tables = 0, links = 0, images = 0, ocrPages = 0, pictureOnlyPages = 0, formValues = 0;

    for (let i = 0; i < numPages; i++) {
      const content = contents[i];
      const lay = layout.pages[i];
      formValues += content.items.filter((it) => it.synthetic).length;

      if (content.hasText) {
        const blocks = placeImages(lay.blocks, pageImages[i]);
        const c = countBlocks(lay.blocks);
        tables += c.tables;
        links += c.links;
        images += pageImages[i].length;
        outPages.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, marginsPt: lay.marginsPt, blocks });
        if (preview.length < 2) preview.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, blocks: blocks.map((b) => (b.kind === "image" ? { kind: "image" as const, widthPt: b.widthPt, heightPt: b.heightPt } : b)) });
        continue;
      }

      // No text layer on this page.
      const own = pageImages[i];
      const pageArea = lay.widthPt * lay.heightPt;
      const bigPicture = own.some((b) => b.widthPt * b.heightPt >= 0.5 * pageArea);
      if (!bigPicture && own.length > 0 && !content.items.some((it) => it.synthetic)) {
        // A page of pictures only (no scan to read): keep the pictures where they were.
        images += own.length;
        outPages.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, marginsPt: { top: 36, right: 36, bottom: 36, left: 36 }, blocks: placeImages([], own) });
        if (preview.length < 2) preview.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, blocks: own.map((b) => ({ kind: "image" as const, widthPt: b.widthPt, heightPt: b.heightPt })), note: "Pictures only on this page." });
        continue;
      }
      if (content.items.some((it) => it.synthetic)) {
        // Only filled-in form values on a page with no other text.
        const c = countBlocks(lay.blocks);
        tables += c.tables;
        outPages.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, marginsPt: lay.marginsPt, blocks: placeImages(lay.blocks, own) });
        continue;
      }

      const page = await pdf.getPage(i + 1);
      alive();
      if (!worker) {
        hooks.status("Starting OCR (first use downloads the language data)…");
        try {
          worker = await createWorker(opts.ocrLanguage, 1);
        } catch (err) {
          if (!hooks.isCurrent()) throw new ConversionCancelled();
          console.error("OCR engine failed to start", err);
          throw new Error("The OCR engine couldn't start. It downloads its language data the first time it is used, so it needs an internet connection; check yours and try again.");
        }
        if (!hooks.isCurrent()) {
          await worker.terminate().catch(() => undefined);
          worker = null;
          throw new ConversionCancelled();
        }
        hooks.registerWorker(worker);
        await worker.setParameters({ tessedit_pageseg_mode: PSM.AUTO });
        alive();
      }
      hooks.status(`Reading page ${i + 1} of ${numPages} with OCR…`);
      hooks.progress(0.5 + i / (numPages * 2));
      // A scan is read from its own picture at full resolution (re-drawing the page at a lower
      // resolution made thin letters such as I, J and 1 disappear); anything else is drawn at 3x.
      const scan = own.filter((b) => b.widthPt * b.heightPt >= 0.5 * pageArea).sort((a, b) => b.widthPt * b.heightPt - a.widthPt * a.heightPt)[0];
      const raw = scan ? new Blob([scan.data as BlobPart], { type: scan.type === "jpg" ? "image/jpeg" : "image/png" }) : await renderPageToJpegBlob(page, 3, 0.92);
      alive();
      const prepared = await preprocessForOcr(raw);
      alive();
      const dev = allowsDevanagari(opts.ocrLanguage);
      let data = (await worker.recognize(prepared, {}, { text: true, blocks: true })).data as unknown as OcrPageLike;
      alive();
      if (needsSecondReading(data, dev)) {
        hooks.status(`Reading page ${i + 1} of ${numPages} again as one block of text…`);
        await worker.setParameters({ tessedit_pageseg_mode: PSM.SINGLE_BLOCK });
        alive();
        const second = (await worker.recognize(prepared, {}, { text: true, blocks: true })).data as unknown as OcrPageLike;
        alive();
        await worker.setParameters({ tessedit_pageseg_mode: PSM.AUTO });
        alive();
        if (readingScore(second, dev) > readingScore(data, dev)) data = second;
      }
      const paragraphs = ocrParagraphs(data);
      const charCount = paragraphs.join("").replace(/\s/g, "").length;
      if (charCount >= MIN_MEANINGFUL_OCR_CHARS) {
        ocrPages++;
        const blocks = ocrBlocks(paragraphs);
        outPages.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, marginsPt: { top: 72, right: 72, bottom: 72, left: 72 }, blocks });
        if (preview.length < 2) preview.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, blocks, note: "Read with OCR." });
      } else {
        // Nothing worth reading: keep the page as a picture instead of forcing noise into the document.
        pictureOnlyPages++;
        const img: ImageBlock = {
          kind: "image",
          data: await bytesOf(raw),
          type: raw.type === "image/png" ? "png" : "jpg",
          widthPt: lay.widthPt - 72,
          heightPt: ((lay.widthPt - 72) * lay.heightPt) / lay.widthPt,
          topPt: 36,
          leftPt: 36,
          rightPt: lay.widthPt - 36,
          description: `Page ${i + 1} of the PDF, kept as a picture`,
        };
        alive();
        outPages.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, marginsPt: { top: 36, right: 36, bottom: 36, left: 36 }, blocks: [img] });
        if (preview.length < 2) preview.push({ widthPt: lay.widthPt, heightPt: lay.heightPt, blocks: [{ kind: "image", widthPt: img.widthPt, heightPt: img.heightPt }], note: "No readable text; kept as a picture." });
      }
    }

    const anything = outPages.some((p) => p.blocks.length > 0);
    if (!anything) return null;

    if (annotationFailures > 0) warnings.push(`Links and form-field values could not be read on ${annotationFailures} ${annotationFailures === 1 ? "page" : "pages"}; the text itself was converted.`);
    if (imageFailures > 0) warnings.push(`${imageFailures} ${imageFailures === 1 ? "picture" : "pictures"} could not be included.`);
    if (renderedImages > 0) warnings.push(`${renderedImages} ${renderedImages === 1 ? "picture was" : "pictures were"} captured as drawn on the page (rotated or unusual pictures), so any text on top of it is part of the picture.`);
    if (scanBackgrounds > 0) warnings.push(`${scanBackgrounds} ${scanBackgrounds === 1 ? "page has" : "pages have"} a scan behind its text; the text was used and the scan picture left out.`);
    if (pictureOnlyPages > 0) warnings.push(`${pictureOnlyPages} ${pictureOnlyPages === 1 ? "page" : "pages"} had no readable text and ${pictureOnlyPages === 1 ? "is" : "are"} kept as a picture.`);

    hooks.status("Building the Word document…");
    hooks.progress(0.95);
    const doc = buildDocxDocument({
      pages: outPages,
      header: layout.header,
      footer: layout.footer,
      title: meaningfulTitle(info?.info?.Title) ?? opts.fallbackTitle,
      author: meaningfulAuthor(info?.info?.Author),
    });
    const blob = await Packer.toBlob(doc);
    alive();
    return { blob, preview, summary: { pages: numPages, tables, images, ocrPages, pictureOnlyPages, formValues, links, warnings } };
  } finally {
    if (worker) {
      hooks.registerWorker(null);
      try {
        await (worker as TesseractWorker).terminate();
      } catch {
        /* already gone */
      }
    }
  }
}
